import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Check, Plus, MapPin, Phone } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#FF3B30";

const HERO_IMG =
  "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxicnV0YWxpc3QlMjBhcmNoaXRlY3R1cmUlMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODc3NDE1ODZ8MA&ixlib=rb-4.1.0&q=85";
const MANIFESTO_IMG =
  "https://images.pexels.com/photos/32466569/pexels-photo-32466569.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const FOOTER_IMG =
  "https://images.pexels.com/photos/5685886/pexels-photo-5685886.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const EASE = [0.22, 1, 0.36, 1];

/* ---------- masked line reveal ---------- */
const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

/* ---------- NAV ---------- */
const Nav = () => (
  <nav
    data-testid="main-nav"
    className="fixed top-0 left-0 w-full z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl"
  >
    <div className="flex items-center justify-between px-6 md:px-16 py-4">
      <a
        href="#top"
        data-testid="nav-logo"
        className="font-mono-b text-xs md:text-sm uppercase tracking-[0.25em] text-zinc-100"
      >
        BOOSTSERVICEBD<span style={{ color: ACCENT }}>.SITE</span>
      </a>
      <a
        href="#pricing"
        data-testid="nav-pricing-link"
        className="font-mono-b text-[11px] md:text-xs uppercase tracking-[0.2em] border border-zinc-600 px-4 md:px-6 py-2 hover:bg-white hover:text-black transition-colors duration-300"
      >
        Get Started
      </a>
    </div>
  </nav>
);

/* ---------- HERO ---------- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden pb-16 md:pb-24 px-6 md:px-16"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="brutalist architecture" className="w-full h-full object-cover grayscale" />
        <div className="absolute inset-0 bg-black/75" />
      </motion.div>

      <div className="relative z-10 w-full">
        <MaskLine delay={0.1} className="font-mono-b text-xs md:text-sm uppercase tracking-[0.3em] text-zinc-400 mb-6 md:mb-10">
          Performance Marketing Studio — Est. Dhaka
        </MaskLine>

        <h1
          data-testid="hero-heading"
          className="font-display font-black uppercase leading-[0.82] tracking-tighter text-[15vw] md:text-[12vw] lg:text-[10.5vw] text-zinc-50"
        >
          <MaskLine delay={0.2}>Boost</MaskLine>
          <MaskLine delay={0.32}>
            Service<span style={{ color: ACCENT }}>.</span>
          </MaskLine>
        </h1>

        <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <MaskLine delay={0.5} className="md:col-span-6 font-mono-b text-sm md:text-base font-light text-zinc-300 leading-relaxed">
            We turn ad spend into revenue. No fluff, no vanity metrics —
            just data-driven campaigns engineered to convert.
          </MaskLine>
          <div className="md:col-span-6 flex md:justify-end">
            <motion.a
              href="#pricing"
              data-testid="hero-cta"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="btn-brutal font-mono-b text-sm uppercase tracking-[0.2em] px-8 py-4 border-2 border-zinc-200 text-zinc-50 inline-flex items-center gap-2"
            >
              See The Package <ArrowUpRight size={18} />
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- MARQUEE ---------- */
const Strip = () => (
  <div data-testid="marquee-strip" className="w-full bg-zinc-100 text-black border-y border-zinc-300 py-4">
    <Marquee speed={60} gradient={false}>
      {Array.from({ length: 4 }).map((_, i) => (
        <span key={i} className="font-mono-b text-sm md:text-base uppercase tracking-[0.25em] mx-8 flex items-center gap-8">
          Data-Driven Marketing <span style={{ color: ACCENT }}>//</span> ROI Obsessed <span style={{ color: ACCENT }}>//</span>{" "}
          High-Conversion <span style={{ color: ACCENT }}>//</span> No Bullshit <span style={{ color: ACCENT }}>//</span>
        </span>
      ))}
    </Marquee>
  </div>
);

/* ---------- MANIFESTO ---------- */
const CHAPTERS = [
  {
    n: "01",
    title: "Strategy First",
    body: "Before a single dollar is spent we map your funnel, audience and offer. Every campaign starts with a clear thesis and a number to beat.",
  },
  {
    n: "02",
    title: "Full-Funnel Ads",
    body: "Meta, Google, TikTok — we build and run high-converting creative and targeting, then optimise relentlessly against real revenue.",
  },
  {
    n: "03",
    title: "Report On Truth",
    body: "You get one honest dashboard: spend, revenue, ROAS. No vanity metrics, no smoke. If it doesn't make money, we kill it.",
  },
];

const Manifesto = () => (
  <section data-testid="manifesto-section" className="py-24 md:py-40 px-6 md:px-16 bg-[#050505]">
    <Reveal className="mb-16 md:mb-24">
      <span className="font-mono-b text-xs md:text-sm uppercase tracking-[0.3em] text-zinc-500">/ How We Work</span>
      <h2 className="font-display font-bold uppercase tracking-tighter text-4xl md:text-6xl mt-4 text-zinc-50">
        Three moves. Zero waste.
      </h2>
    </Reveal>

    {CHAPTERS.map((c, i) => (
      <Reveal key={c.n} delay={i * 0.05}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 py-10 md:py-14 border-t border-zinc-800 group">
          <div className="md:col-span-3">
            <span
              className="font-display font-black text-6xl md:text-8xl leading-none text-zinc-800 group-hover:text-[color:var(--accent)] transition-colors duration-500"
              style={{ "--accent": ACCENT }}
            >
              {c.n}
            </span>
          </div>
          <div className="md:col-span-6">
            <h3 className="font-display font-bold uppercase tracking-tight text-2xl md:text-4xl text-zinc-50">{c.title}</h3>
          </div>
          <div className="md:col-span-3">
            <p className="font-mono-b text-sm font-light text-zinc-400 leading-relaxed">{c.body}</p>
          </div>
        </div>
      </Reveal>
    ))}
    <div className="border-t border-zinc-800" />
  </section>
);

/* ---------- STATS ---------- */
const STATS = [
  { v: "2.5X", l: "Avg. Return On Ad Spend" },
  { v: "$10M+", l: "Managed Ad Spend" },
  { v: "120+", l: "Campaigns Launched" },
  { v: "48H", l: "Onboarding To Live" },
];

const Stats = () => (
  <section data-testid="stats-section" className="bg-zinc-900 border-y border-zinc-800">
    <div className="grid grid-cols-1 md:grid-cols-4">
      {STATS.map((s, i) => (
        <Reveal
          key={s.l}
          delay={i * 0.08}
          className="border-b md:border-b-0 md:border-r border-zinc-800 last:border-r-0 p-8 md:p-12"
        >
          <div className="font-display font-black text-5xl md:text-6xl tracking-tighter text-zinc-50">{s.v}</div>
          <div className="font-mono-b text-[11px] md:text-xs uppercase tracking-[0.2em] text-zinc-500 mt-4">{s.l}</div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------- PRICING ---------- */
const TIERS = [
  {
    id: "starter_boost",
    name: "Starter\nBoost",
    price: "$1",
    suffix: "one-time",
    tag: "Try Us",
    featured: false,
    blurb: "A no-risk first step. We audit your funnel and hand you a growth plan.",
    features: ["30-min strategy call", "Funnel & ad teardown", "Actionable growth plan", "Zero commitment"],
  },
  {
    id: "basic_marketing",
    name: "Basic\nMarketing",
    price: "$2500",
    suffix: "/ month",
    tag: "Most Popular",
    featured: true,
    blurb: "Everything a growing brand needs to run profitable paid acquisition, managed end to end.",
    features: [
      "Meta, Google & TikTok ads",
      "Full funnel strategy",
      "Conversion-focused creatives",
      "Weekly optimisation & reporting",
      "Dedicated performance manager",
      "Cancel anytime — no lock-in",
    ],
  },
  {
    id: "growth_marketing",
    name: "Growth\nMarketing",
    price: "$5000",
    suffix: "/ month",
    tag: "Scale",
    featured: false,
    blurb: "Aggressive multi-channel growth for brands ready to dominate their market.",
    features: [
      "Everything in Basic, doubled",
      "Advanced creative testing at scale",
      "Landing page & CRO sprints",
      "Email & retention flows",
      "Bi-weekly strategy sessions",
      "Priority Slack support",
    ],
  },
];

const Pricing = () => {
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState("");

  const handleCheckout = async (id) => {
    setLoadingId(id);
    setError("");
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        package_id: id,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError("Could not start checkout. Please try again.");
        setLoadingId(null);
      }
    } catch (e) {
      setError("Payment could not be started. Please try again.");
      setLoadingId(null);
    }
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-40 px-6 md:px-16 bg-[#050505]">
      <Reveal className="mb-12 md:mb-20 text-center">
        <span className="font-mono-b text-xs md:text-sm uppercase tracking-[0.3em] text-zinc-500">
          / Pick Your Level
        </span>
        <h2 className="font-display font-bold uppercase tracking-tighter text-4xl md:text-6xl mt-4 text-zinc-50">
          Plans that scale with you
        </h2>
      </Reveal>

      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {TIERS.map((t, i) => (
          <Reveal key={t.id} delay={i * 0.08}>
            <div
              data-testid={`pricing-card-${t.id}`}
              className={`h-full flex flex-col bg-black ${
                t.featured ? "border-2" : "border border-zinc-800"
              }`}
              style={t.featured ? { borderColor: ACCENT } : undefined}
            >
              <div className="p-8 border-b border-zinc-800">
                <div className="flex items-center justify-between mb-6">
                  <span
                    className="font-mono-b text-[10px] uppercase tracking-[0.2em] px-3 py-1 border"
                    style={{
                      color: t.featured ? "#fff" : "#a1a1aa",
                      borderColor: t.featured ? ACCENT : "#3f3f46",
                      backgroundColor: t.featured ? ACCENT : "transparent",
                    }}
                  >
                    {t.tag}
                  </span>
                </div>
                <h3 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl text-zinc-50 whitespace-pre-line leading-[0.9]">
                  {t.name}
                </h3>
                <div className="mt-6 flex items-end gap-2">
                  <span className="font-display font-black text-5xl md:text-6xl tracking-tighter text-zinc-50">
                    {t.price}
                  </span>
                  <span className="font-mono-b text-[11px] uppercase tracking-widest text-zinc-500 mb-2">
                    {t.suffix}
                  </span>
                </div>
                <p className="font-mono-b text-sm font-light text-zinc-400 mt-5 leading-relaxed">{t.blurb}</p>
              </div>

              <div className="p-8 flex-1">
                <ul className="space-y-4">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check size={16} style={{ color: ACCENT }} className="mt-0.5 shrink-0" />
                      <span className="font-mono-b text-sm text-zinc-200">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-8 pt-0">
                <button
                  data-testid={`checkout-button-${t.id}`}
                  onClick={() => handleCheckout(t.id)}
                  disabled={loadingId === t.id}
                  className={`btn-brutal w-full font-display font-bold uppercase tracking-[0.12em] text-base md:text-lg py-5 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${
                    t.featured ? "text-white" : "text-zinc-50 border-2 border-zinc-200"
                  }`}
                  style={t.featured ? { backgroundColor: ACCENT } : undefined}
                >
                  {loadingId === t.id ? "Redirecting…" : "Pay With Card"}
                  {loadingId !== t.id && <Plus size={18} />}
                </button>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {error && (
        <p data-testid="checkout-error" className="font-mono-b text-xs text-center mt-8" style={{ color: ACCENT }}>
          {error}
        </p>
      )}
      <p className="font-mono-b text-[11px] uppercase tracking-widest text-zinc-600 text-center mt-10">
        Secure checkout powered by Stripe
      </p>
    </section>
  );
};

/* ---------- FOOTER ---------- */
const Footer = () => (
  <footer data-testid="footer" className="relative overflow-hidden bg-black border-t border-zinc-800 px-6 md:px-16 py-20 md:py-28">
    <div className="absolute inset-0 z-0">
      <img src={FOOTER_IMG} alt="team" className="w-full h-full object-cover grayscale" />
      <div className="absolute inset-0 bg-black/90" />
    </div>
    <div className="relative z-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-12">
        <div>
          <h2 className="font-display font-black uppercase tracking-tighter text-6xl md:text-8xl text-zinc-50 leading-[0.85]">
            Let's
            <br />
            Build<span style={{ color: ACCENT }}>.</span>
          </h2>
          <a
            href="#pricing"
            className="btn-brutal inline-flex items-center gap-2 mt-8 font-mono-b text-sm uppercase tracking-[0.2em] px-8 py-4 border-2 border-zinc-200 text-zinc-50"
          >
            Start Now <ArrowUpRight size={18} />
          </a>
        </div>
        <div className="font-mono-b text-xs uppercase tracking-[0.2em] text-zinc-500 space-y-2">
          <p className="text-zinc-300">BOOSTSERVICEBD.SITE</p>
          <p>Performance Marketing Studio</p>
        </div>
      </div>

      {/* contact + legal */}
      <div className="mt-16 pt-10 border-t border-zinc-800 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div data-testid="footer-address">
          <p className="font-mono-b text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Support Address</p>
          <p className="font-mono-b text-sm text-zinc-300 leading-relaxed flex items-start gap-2">
            <MapPin size={16} style={{ color: ACCENT }} className="mt-0.5 shrink-0" />
            2077 Shela Boulevard,<br />Portsmouth, OH 45662, US
          </p>
        </div>
        <div data-testid="footer-phone">
          <p className="font-mono-b text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Support Phone</p>
          <a
            href="tel:+12088973044"
            className="font-mono-b text-sm text-zinc-300 flex items-center gap-2 hover:text-white transition-colors"
          >
            <Phone size={16} style={{ color: ACCENT }} className="shrink-0" />
            +1 (208) 897-3044
          </a>
        </div>
        <div data-testid="footer-descriptor">
          <p className="font-mono-b text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Statement Descriptor</p>
          <p className="font-mono-b text-sm text-zinc-300 leading-relaxed">
            BOOSTSERVICEBD.NET AND BOOSTSERVICEBD.SITE
          </p>
        </div>
      </div>

      <div className="mt-10 pt-6 border-t border-zinc-800 flex flex-col md:flex-row md:justify-between gap-2">
        <p className="font-mono-b text-[11px] uppercase tracking-[0.2em] text-zinc-600">
          © {new Date().getFullYear()} BOOSTSERVICEBD.SITE — All Rights Reserved
        </p>
        <p className="font-mono-b text-[11px] uppercase tracking-[0.2em] text-zinc-600">
          Secure payments via Stripe
        </p>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <div className="bg-[#050505] text-zinc-50">
      <Nav />
      <Hero />
      <Strip />
      <Manifesto />
      <Stats />
      <Pricing />
      <Footer />
    </div>
  );
}
